import { InheritanceDiagramNode } from './buildOrganizationInheritanceDiagram';

export type DiagramLeafMode = 'none' | 'repos' | 'members';

export const GROUP_NODE_WIDTH = 228;
export const GROUP_NODE_HEIGHT = 96;
export const REPO_NODE_WIDTH = 200;
export const REPO_NODE_HEIGHT = 62;
export const MEMBER_NODE_WIDTH = 260;
export const MEMBER_NODE_HEIGHT = 58;

/** @deprecated Use GROUP_NODE_WIDTH */
export const TREE_NODE_WIDTH = GROUP_NODE_WIDTH;
/** @deprecated Use GROUP_NODE_HEIGHT */
export const TREE_NODE_HEIGHT = GROUP_NODE_HEIGHT;

export const TREE_LEVEL_GAP = 120;
export const TREE_SIBLING_GAP = 28;
export const TREE_CANVAS_PADDING = 32;
export const TREE_COLUMN_WIDTH = GROUP_NODE_WIDTH + TREE_LEVEL_GAP;
const EDGE_CORNER_RADIUS = 12;

export type TreeGraphNode = {
  id: string;
  data: InheritanceDiagramNode;
  parentId?: string;
  depth: number;
  expandable: boolean;
  hasHiddenChildren: boolean;
  /** Children revealed on expand in the active leaf mode (groups + mode leaves). */
  hiddenChildCount: number;
  /** Leaf children the active leaf mode never renders (suppressed by mode). */
  hiddenLeafCount: number;
  isExpanded: boolean;
};

export type TreeGraph = {
  nodes: TreeGraphNode[];
  edges: Array<{ from: string; to: string }>;
};

export type PositionedTreeNode = TreeGraphNode & {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TreeLayout = {
  nodes: PositionedTreeNode[];
  edges: Array<{
    from: string;
    to: string;
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    targetKind: InheritanceDiagramNode['nodeKind'];
  }>;
  width: number;
  height: number;
};

export function nodePortPoint(
  node: Pick<PositionedTreeNode, 'x' | 'y' | 'width' | 'height'>,
  side: 'left' | 'right',
): { x: number; y: number } {
  const y = node.y + node.height / 2;
  if (side === 'right') {
    return { x: node.x + node.width, y };
  }
  return { x: node.x, y };
}

export function treeEdgePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): string {
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;

  if (Math.abs(dy) < 0.5) {
    return `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  }

  if (dx <= EDGE_CORNER_RADIUS * 2) {
    const midX = sourceX + dx / 2;
    return `M ${sourceX} ${sourceY} C ${midX} ${sourceY}, ${midX} ${targetY}, ${targetX} ${targetY}`;
  }

  const radius = Math.min(EDGE_CORNER_RADIUS, Math.abs(dy) / 2, dx / 4);
  const midX = sourceX + dx / 2;
  const verticalDir = dy > 0 ? 1 : -1;

  return [
    `M ${sourceX} ${sourceY}`,
    `H ${midX - radius}`,
    `Q ${midX} ${sourceY}, ${midX} ${sourceY + verticalDir * radius}`,
    `V ${targetY - verticalDir * radius}`,
    `Q ${midX} ${targetY}, ${midX + radius} ${targetY}`,
    `H ${targetX}`,
  ].join(' ');
}

export function treeNodeDimensions(nodeKind: InheritanceDiagramNode['nodeKind']): {
  width: number;
  height: number;
} {
  if (nodeKind === 'component') {
    return { width: REPO_NODE_WIDTH, height: REPO_NODE_HEIGHT };
  }
  if (nodeKind === 'member') {
    return { width: MEMBER_NODE_WIDTH, height: MEMBER_NODE_HEIGHT };
  }
  return { width: GROUP_NODE_WIDTH, height: GROUP_NODE_HEIGHT };
}

function isLeafNodeKind(nodeKind: InheritanceDiagramNode['nodeKind']): boolean {
  return nodeKind === 'component' || nodeKind === 'member';
}

function leafNodeKindForMode(leafMode: DiagramLeafMode): InheritanceDiagramNode['nodeKind'] | undefined {
  if (leafMode === 'repos') {
    return 'component';
  }
  if (leafMode === 'members') {
    return 'member';
  }
  return undefined;
}

function countHiddenLeafChildren(
  node: InheritanceDiagramNode,
  leafMode: DiagramLeafMode,
): number {
  // Leaf children the active mode never renders (so they stay hidden regardless
  // of expand state): in 'none' mode every leaf child, otherwise the leaves of
  // the kind the mode does not show. This is disjoint from hiddenChildCount.
  const leafKind = leafNodeKindForMode(leafMode);
  return node.children.filter(
    child => isLeafNodeKind(child.nodeKind) && child.nodeKind !== leafKind,
  ).length;
}

function visibleChildren(
  node: InheritanceDiagramNode,
  leafMode: DiagramLeafMode,
): InheritanceDiagramNode[] {
  const leafKind = leafNodeKindForMode(leafMode);
  if (!leafKind) {
    return node.children.filter(child => !isLeafNodeKind(child.nodeKind));
  }
  return node.children.filter(
    child => child.nodeKind === leafKind || !isLeafNodeKind(child.nodeKind),
  );
}

function isExpandable(node: InheritanceDiagramNode, leafMode: DiagramLeafMode): boolean {
  return !isLeafNodeKind(node.nodeKind) && visibleChildren(node, leafMode).length > 0;
}

export function buildVisibleTreeGraph(
  root: InheritanceDiagramNode,
  expandedIds: Set<string>,
  leafMode: DiagramLeafMode = 'repos',
): TreeGraph {
  const nodes: TreeGraphNode[] = [];
  const edges: Array<{ from: string; to: string }> = [];

  const visit = (node: InheritanceDiagramNode, parentId: string | undefined, depth: number) => {
    const expandable = isExpandable(node, leafMode);
    const isExpanded = expandable && expandedIds.has(node.id);
    const hasHiddenChildren = expandable && !isExpanded;
    const hiddenLeafCount = countHiddenLeafChildren(node, leafMode);
    // Only what expanding actually reveals in the current mode, so the "+N
    // hidden" badge never exceeds the nodes a click would show.
    const hiddenChildCount = hasHiddenChildren
      ? visibleChildren(node, leafMode).length
      : 0;

    nodes.push({
      id: node.id,
      data: node,
      parentId,
      depth,
      expandable,
      hasHiddenChildren,
      hiddenChildCount,
      hiddenLeafCount,
      isExpanded,
    });

    if (isExpanded) {
      for (const child of visibleChildren(node, leafMode)) {
        edges.push({ from: node.id, to: child.id });
        visit(child, node.id, depth + 1);
      }
    }
  };

  visit(root, undefined, 0);
  return { nodes, edges };
}

type SubtreeLayout = {
  offset: number;
  span: number;
};

export function layoutTreeGraph(graph: TreeGraph): TreeLayout {
  if (graph.nodes.length === 0) {
    return { nodes: [], edges: [], width: 0, height: 0 };
  }

  const nodeById = new Map(graph.nodes.map(node => [node.id, node]));
  const childrenByParent = new Map<string, string[]>();
  for (const edge of graph.edges) {
    const bucket = childrenByParent.get(edge.from) ?? [];
    bucket.push(edge.to);
    childrenByParent.set(edge.from, bucket);
  }

  const nodeHeight = (nodeId: string) =>
    treeNodeDimensions(nodeById.get(nodeId)!.data.nodeKind).height;

  const roots = graph.nodes.filter(node => !node.parentId);
  const yById = new Map<string, number>();
  const cursor = { offset: 0 };

  const layoutNode = (nodeId: string): SubtreeLayout => {
    const childIds = childrenByParent.get(nodeId) ?? [];
    const height = nodeHeight(nodeId);

    if (childIds.length === 0) {
      const offset = cursor.offset;
      cursor.offset += height + TREE_SIBLING_GAP;
      yById.set(nodeId, offset);
      return { offset, span: height };
    }

    const childLayouts = childIds.map(childId => layoutNode(childId));
    const first = childLayouts[0];
    const last = childLayouts[childLayouts.length - 1];
    const span = last.offset + nodeHeight(childIds[childIds.length - 1]) - first.offset;
    const offset = first.offset + span / 2 - height / 2;
    yById.set(nodeId, offset);
    return { offset, span };
  };

  roots.forEach(root => layoutNode(root.id));

  const positionedNodes: PositionedTreeNode[] = graph.nodes.map(node => {
    const { width, height } = treeNodeDimensions(node.data.nodeKind);
    return {
      ...node,
      x: node.depth * TREE_COLUMN_WIDTH,
      y: yById.get(node.id) ?? 0,
      width,
      height,
    };
  });

  const positionedById = new Map(positionedNodes.map(node => [node.id, node]));
  const maxX = Math.max(...positionedNodes.map(node => node.x + node.width), GROUP_NODE_WIDTH);
  const maxY = Math.max(...positionedNodes.map(node => node.y + node.height), GROUP_NODE_HEIGHT);

  const edges = graph.edges.map(edge => {
    const source = positionedById.get(edge.from)!;
    const target = positionedById.get(edge.to)!;
    const sourcePoint = nodePortPoint(source, 'right');
    const targetPoint = nodePortPoint(target, 'left');
    return {
      from: edge.from,
      to: edge.to,
      sourceX: sourcePoint.x,
      sourceY: sourcePoint.y,
      targetX: targetPoint.x,
      targetY: targetPoint.y,
      targetKind: target.data.nodeKind,
    };
  });

  return {
    nodes: positionedNodes,
    edges,
    width: maxX + TREE_CANVAS_PADDING,
    height: maxY + TREE_CANVAS_PADDING,
  };
}

export function collectExpandableNodeIds(
  root: InheritanceDiagramNode,
  leafMode: DiagramLeafMode = 'repos',
): string[] {
  const ids: string[] = [];
  const visit = (node: InheritanceDiagramNode) => {
    if (isExpandable(node, leafMode)) {
      ids.push(node.id);
    }
    visibleChildren(node, leafMode).forEach(visit);
  };
  visit(root);
  return ids;
}

export function defaultExpandedIds(
  root: InheritanceDiagramNode,
  leafMode: DiagramLeafMode = 'repos',
): Set<string> {
  return new Set(isExpandable(root, leafMode) ? [root.id] : []);
}

export function collectAllNodeIds(root: InheritanceDiagramNode): string[] {
  const ids: string[] = [root.id];
  const visit = (node: InheritanceDiagramNode) => {
    for (const child of node.children) {
      ids.push(child.id);
      visit(child);
    }
  };
  visit(root);
  return ids;
}
